import { Link } from "@tanstack/react-router";
import { useAuth0 } from "@auth0/auth0-react";
import { useMe } from "../api/hooks";
import { OrgSwitcher } from "./OrgSwitcher";
import { ThemeToggle } from "./theme";

// Fixed-width vertical navigation. Replaces the horizontal NavBar.
// Layout responsibility: this component renders itself; the rest of
// the layout (main content scroll, mobile drawer, etc.) lives in the
// router root.

const NAV_ITEMS = [
  { to:"/", label:"Overview", end: true },
  { to:"/portfolio", label:"Portfolio" },
  { to:"/alerts", label:"Alerts" },
  { to:"/domains", label:"Domains" },
  { to:"/dns-monitor", label:"DNS monitor" },
  { to:"/preview", label:"Preview" },
  { to:"/validator", label:"Validator" },
  { to:"/quarantine", label:"Quarantine" },
] as const;

export function Sidebar({ onOpenSearch }: { onOpenSearch?: () => void }) {
  const { user, logout } = useAuth0();
  const me = useMe();
  const isAdmin = me.data?.roles.includes("admin") ?? false;
  const isPlatformAdmin = me.data?.isPlatformAdmin ?? false;
  // Mac users see ⌘, others see Ctrl. Respects the global keyboard
  // shortcut wired up in the router root.
  const modifierKey =
    typeof navigator !== "undefined" && /Mac/i.test(navigator.platform) ? "⌘" : "Ctrl";

  return (
    <aside className= "hidden h-screen w-60 shrink-0 flex-col border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 sm:flex">
      <div className= "px-4 py-4 border-b border-slate-200 dark:border-slate-800">
        <Link to= "/" className= "inline-flex items-center gap-2">
          <img
            src= "/etus-logo.png"
            alt= ""
            className= "h-9 w-9 rounded-lg shadow-sm"
          />
          <span className= "text-xl font-semibold text-slate-900 dark:text-slate-100">
            DMARC Monitor
          </span>
        </Link>
      </div>

      <OrgSwitcher />

      {onOpenSearch ? (
        <div className= "px-2 pt-3">
          <button
            type= "button"
            onClick={onOpenSearch}
            className= "flex w-full items-center justify-between gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-3 py-1.5 text-sm text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-700 dark:hover:text-slate-200"
          >
            <span className= "flex items-center gap-2">
              <svg
                viewBox= "0 0 24 24"
                fill= "none"
                stroke= "currentColor"
                strokeWidth= "2"
                strokeLinecap= "round"
                strokeLinejoin= "round"
                className= "h-3.5 w-3.5"
                aria-hidden= "true"
              >
                <circle cx= "11" cy= "11" r= "7" />
                <line x1= "21" y1= "21" x2= "16.65" y2= "16.65" />
              </svg>
              Search
            </span>
            <kbd className= "rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-1 font-sans text-[10px] text-slate-500 dark:text-slate-400">
              {modifierKey}K
            </kbd>
          </button>
        </div>
      ) : null}

      <nav className= "flex-1 overflow-y-auto px-2 py-3">
        <ul className= "space-y-0.5">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <NavItem to={item.to} end={"end" in item ? item.end : undefined}>
                {item.label}
              </NavItem>
            </li>
          ))}
          <li>
            <NavItem to= "/settings">Settings</NavItem>
          </li>
          {isAdmin ? (
            <>
              <li className= "mt-4 px-3 text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Admin
              </li>
              <li>
                <NavItem to= "/admin">Admin</NavItem>
              </li>
            </>
          ) : null}
          {isPlatformAdmin ? (
            <>
              <li className= "mt-4 px-3 text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Platform
              </li>
              <li>
                <NavItem to= "/platform">Tenants</NavItem>
              </li>
            </>
          ) : null}
        </ul>
      </nav>

      <div className= "border-t border-slate-200 dark:border-slate-800 px-3 py-3 space-y-2">
        <div className= "flex items-center justify-between gap-2">
          <span className= "text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
            Theme
          </span>
          <ThemeToggle />
        </div>
        <div className= "truncate text-xs text-slate-600 dark:text-slate-400" title={user?.email}>
          {user?.email ?? "—"}
        </div>
        <button
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          className= "w-full rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          Sign out
        </button>
      </div>
    </aside>
  );
}

function NavItem({
  to,
  end,
  children,
}: {
  to: string;
  end?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      activeOptions={end ? { exact: true } : undefined}
      className= "block rounded px-3 py-1.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100"
      activeProps={{
        className:"block rounded px-3 py-1.5 text-sm font-medium bg-slate-900 text-white hover:bg-slate-900 hover:text-white",
      }}
    >
      {children}
    </Link>
  );
}
